import React from "react";
import { queryCache, useMutation } from "react-query";
import { useTranslation } from "react-i18next";

import { SelectInput, showNotification } from "fe-shared/src/components";
import { CloseIcon } from "fe-shared/src/components/Icons";
import { PickupRequestPriority } from "fe-shared-requests/src/types";
import { isPickupRequestEditable } from "fe-shared-requests/src/helpers/pickupRequestStatus";

import { REQUESTS_QUERY_KEY } from "consts";
import { PickupRequestListItem } from "types";
import { updatePickupRequestPriority } from "routes/Requests/modules/api";
import PriorityIcon from "fe-shared-requests/src/components/PriorityIcon";

import styles from "./PriorityGridCell.module.scss";

interface Props {
  data: PickupRequestListItem;
  canEditPriority: boolean;
}

const PRIORITIES = Object.values(PickupRequestPriority);

const PriorityGridCell = ({ data, canEditPriority }: Props) => {
  const { t } = useTranslation();
  const [changePriority, { isLoading }] = useMutation(updatePickupRequestPriority, {
    onSuccess: () => queryCache.invalidateQueries(REQUESTS_QUERY_KEY),
    onError: () => {
      showNotification(
        t("pickupRequests.grid.priorityUpdateError"),
        undefined,
        CloseIcon
      );
    },
  });

  const priorityLabel = (priority: PickupRequestPriority) =>
    t(`pickupRequests.priorities.${priority}`, { defaultValue: priority });

  if (!canEditPriority || !isPickupRequestEditable(data.statusCode, true)) {
    return (
      <span className={styles.priority}>
        <PriorityIcon priority={data.priorityCode} />
        {priorityLabel(data.priorityCode)}
      </span>
    );
  }

  return (
    <SelectInput
      id={`priority-${data.requestNumber}`}
      wrapperClassName={styles.prioritySelect}
      value={data.priorityCode}
      isLoading={isLoading}
      options={PRIORITIES.map((priority) => ({
        value: priority,
        label: priorityLabel(priority),
        labelIcon: <PriorityIcon priority={priority} />,
      }))}
      onChange={(_event, value) =>
        changePriority({
          requestNumber: data.requestNumber,
          priority: value as PickupRequestPriority,
        })}
    />
  );
};

export default PriorityGridCell;
